<script>
import BlockingContainer from '@components/BlockingContainer';
import editFormChange from '@mixins/editFormChange';
import AllowanceModel from '@models/Allowance';
import CountryModel from '@models/Country';
import isFunction from 'lodash/isFunction';
import CountryForm from '@views/countries/form';

export default {
  components: {
    BlockingContainer,
    CountryForm,
  },
  mixins: [editFormChange],
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
  },
  data() {
    return {
      allowance: {
        country_id: null,
        meals: null,
        meals_24: null,
        overnight_stay: null,
      },
      countries: [],
      showCountryModal: false,
    };
  },
  mounted() {
    if (this.loadedId) {
      this.$wait.start('allowance loading');
      AllowanceModel.find(this.loadedId).then((response) => {
        this.allowance = Object.assign({}, this.allowance, response.allowance);
        this.$wait.end('allowance loading');
        this.$nextTick(() => {
          this.initChangeWatcher(['allowance'], this.validate);
        });
      });
    }

    // fetch countries
    this.$wait.start('allowance loading');
    CountryModel.page(1)
      .where('not_in_allowance', true)
      .orderBy('order')
      .limit(100)
      .get()
      .then((response) => {
        this.countries = response.data;
        this.$wait.end('allowance loading');
      });
  },
  methods: {
    validate(callback) {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$wait.start('allowance loading');
          this.submit(callback);
        }
      });
    },
    submit(callback) {
      if (this.$route.params.id) {
        this.allowance.id = this.$route.params.id;
      }
      new AllowanceModel(this.allowance)
        .save()
        .then((response) => {
          this.$emit('submitted', response.allowance);
          if (callback && isFunction(callback)) callback();
          this.$wait.end('allowance loading');
        })
        .catch(() => this.$wait.end('allowance loading'));
    },
    countryCreated(country) {
      this.countries.push(country);
      this.$nextTick(() => {
        this.allowance.country_id = country.id;
        this.showCountryModal = false;
      });
    },
  },
};
</script>

<template>
  <div>
    <BlockingContainer v-if="$wait.is('allowance loading')"></BlockingContainer>
    <b-form data-vv-scope="allowance-form" @submit.prevent="validate">
      <b-row>
        <b-col lg="10" sm="12">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.country') }}:
              <span class="status-star text-danger">*</span>
            </template>
            <b-form-select
              v-model="allowance.country_id"
              v-validate="'required'"
              data-vv-value-path="localValue"
              data-vv-name="country_id"
              :data-vv-as="$t('fields.country')"
              :state="errors.has('country_id', 'allowance-form') ? false : null"
              aria-describedby="input-feedback-country_id"
            >
              <option :value="null">{{ $t('placeholders.select_country') }}</option>
              <option v-for="country in countries" :key="`countrySelect${country.id}`" :value="country.id">
                {{ country.name_de }}
              </option>
            </b-form-select>
            <b-form-invalid-feedback id="input-feedback-country_id">
              <template v-show="errors.has('country_id', 'allowance-form')">{{ errors.first('country_id', 'allowance-form') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col lg="2" sm="12">
          <b-form-group>
            <legend tabindex="-1" class="bv-no-focus-ring col-form-label pt-0">&nbsp;</legend>
            <b-btn variant="dark" class="w-100" @click="showCountryModal = true">
              {{ $t('titles.create_country') }}
            </b-btn>
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.meals_8') }}:
          <span class="status-star text-danger">*</span>
        </template>
        <b-form-input
          v-model="allowance.meals"
          v-validate="'required|numeric'"
          data-vv-value-path="localValue"
          data-vv-name="meals"
          :data-vv-as="$t('fields.meals_8')"
          :state="errors.has('meals', 'allowance-form') ? false : null"
          aria-describedby="input-feedback-meals"
          :placeholder="$t('placeholders.meals')"
        ></b-form-input>
        <b-form-invalid-feedback id="input-feedback-meals">
          <template v-show="errors.has('country_id', 'allowance-form')">{{ errors.first('country_id', 'allowance-form') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.meals_24') }}:
          <span class="status-star text-danger">*</span>
        </template>
        <b-form-input
          v-model="allowance.meals_24"
          v-validate="'required|numeric'"
          data-vv-value-path="localValue"
          data-vv-name="meals_24"
          :data-vv-as="$t('fields.meals_24')"
          :state="errors.has('meals_24', 'allowance-form') ? false : null"
          aria-describedby="input-feedback-meals_24"
          :placeholder="$t('placeholders.meals_24')"
        ></b-form-input>
        <b-form-invalid-feedback id="input-feedback-meals_24">
          <template v-show="errors.has('meals_24', 'allowance-form')">{{ errors.first('meals_24', 'allowance-form') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.overnight_stay') }}:
          <span class="status-star text-danger">*</span>
        </template>
        <b-form-input
          v-model="allowance.overnight_stay"
          v-validate="'required|numeric'"
          data-vv-value-path="localValue"
          data-vv-name="overnight_stay"
          :data-vv-as="$t('fields.overnight_stay')"
          :state="errors.has('overnight_stay', 'allowance-form') ? false : null"
          aria-describedby="input-feedback-overnight_stay"
          :placeholder="$t('placeholders.overnight_stay')"
        ></b-form-input>
        <b-form-invalid-feedback id="input-feedback-overnight_stay">
          <template v-show="errors.has('overnight_stay', 'allowance-form')">{{ errors.first('overnight_stay', 'allowance-form') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-button type="submit" :disabled="errors.any()" :variant="'outline-' + (!errors.any() ? 'dark' : 'danger')">
        {{ $t('actions.submit') }}
      </b-button>
    </b-form>
    <b-modal v-if="showCountryModal" v-model="showCountryModal" size="lg" hide-footer :title="$t('titles.create_country')" static @hidden="$emit('hidden')">
      <CountryForm @submitted="countryCreated" />
    </b-modal>
  </div>
</template>
